import addStepIcon from '../../../assets/images/AddStepIcon.svg';
import stepIcon from '../../../assets/images/StepIcon.svg';
import { flowBuilderConstants } from '../../../constants/FlowBuilder';
import { TranslateHook } from '../../../modules/journey-canvas/utility/TypeDefs';
import { CanvasElement } from '../canvas-element';
import { BaseFlowConfig } from '../TypeDefs';

/**
 * Draw a rounded rectangle using `roundRect` but on firefox uses `line` and `arc` to draw
 * @param ctx canvas 2d context
 * @param x
 * @param y
 * @param width
 * @param height
 * @param radius
 */
export const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(x, y, width, height, radius);
  } else {
    // firefox don't have implementation of roundRect use arc to draw the rounded corners
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }
};

/**
 * @method drawRoundedTriangle
 * @description Draw a rounded triangle on the canvas.
 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
 * @param {number} x - The x-coordinate of the top-left corner of the triangle.
 * @param {number} y - The y-coordinate of the top-left corner of the triangle.
 * @param {number} width - The width of the triangle.
 * @param {number} height - The height of the triangle.
 * @param {number} radius - The radius of the rounded corners.
 */
export const drawRoundedTriangle = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  const centerY = y + height / 2;
  ctx.beginPath();

  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, centerY - radius * 0.8);
  ctx.quadraticCurveTo(x + width, centerY, x + width - radius, centerY + radius * 0.8);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

/**
 * @method drawCanvasImage
 * @description Draws an image on a canvas.
 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
 * @param {HTMLImageElement} image - The image to be drawn.
 * @param {number} x - The x-coordinate of the top-left corner of the destination rectangle.
 * @param {number} y - The y-coordinate of the top-left corner of the destination rectangle.
 * @param {number} w - The width of the destination rectangle.
 * @param {number} h - The height of the destination rectangle.
 */
export const drawCanvasImage = (ctx: CanvasRenderingContext2D, image: HTMLImageElement, x: number, y: number, w: number, h: number) => {
  if (image.getAttribute('data-loaded') === '1') {
    ctx.drawImage(image, x, y, w, h);
  } else {
    image.onload = () => {
      ctx.drawImage(image, x, y, w, h);
      image.setAttribute('data-loaded', '1');
      image.onload = null;
    };
  }
};

/**
 * @method setImageSrc
 * @description Set the source of an image element and handle the onload event.
 * @param {HTMLImageElement} image - The image element.
 * @param {string} src - The source URL of the image.
 */
export const setImageSrc = (image: HTMLImageElement, src: string) => {
  image.onload = () => {
    image.setAttribute('data-loaded', '1');
    image.onload = null;
  };
  image.setAttribute('data-loaded', '0');
  image.src = src;
};

const stepIconImage = new Image();
setImageSrc(stepIconImage, stepIcon);
const addIconImage = new Image();
setImageSrc(addIconImage, addStepIcon);

/**
 * @method canvasDraw
 * @description Draw the canvas with the provided elements and configurations.
 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
 * @param {number} width - The width of the canvas.
 * @param {number} height - The height of the canvas.
 * @param {number} translateX - The translation on the x-axis.
 * @param {number} translateY - The translation on the y-axis.
 * @param {number} zoomLevel - The zoom level of the canvas.
 * @param {CanvasElement[]} canvasElements - The elements to be drawn on the canvas.
 * @param {BaseFlowConfig} flowConfig - The configuration for the flow.
 * @param {boolean} startBtnSelected - Indicates if the start button is selected.
 * @param {boolean} startBtnVisible - Indicates if the start button is visible.
 * @param {boolean} initiateStepCardVisible - Indicates if the initiate step card is visible.
 */
export const canvasDraw = (
  {
    ctx,
    width,
    height,
    translateX,
    translateY,
    zoomLevel,
    canvasElements,
    flowConfig,
    startBtnSelected,
    startBtnVisible,
    initiateStepCardVisible,
    translate
  }: {
    ctx: CanvasRenderingContext2D;
    width: number;
    height: number;
    translateX: number;
    translateY: number;
    zoomLevel: number;
    canvasElements: CanvasElement[];
    flowConfig: BaseFlowConfig;
    startBtnSelected: boolean;
    startBtnVisible: boolean;
    initiateStepCardVisible: boolean;
    translate: TranslateHook
  }
) => {
  // reset the zoom  level and translate to apply new zoom
  ctx.setTransform(
    window.devicePixelRatio,
    0,
    0,
    window.devicePixelRatio,
    0,
    0
  );
  ctx.clearRect(0, 0, width, height);
  // translate canvas to have panning effect
  ctx.translate(
    translateX,
    translateY
  );
  ctx.scale(zoomLevel, zoomLevel);
  // draw the elements
  canvasElements.forEach((e) => e.draw(ctx));
  if (startBtnVisible) {
    // draw start selection status
    if (startBtnSelected) {
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = flowConfig.startBtnSelectionColor;
      drawRoundedRect(
        ctx,
        flowBuilderConstants.startBtnPosition.x - 25,
        flowBuilderConstants.startBtnPosition.y - 20,
        flowConfig.startBtnWidth + 50,
        flowConfig.startBtnHeight + 40,
        15
      );
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = flowConfig.startBtnSelectionColor;
      ctx.setLineDash([4, 2]);
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    // draw the start
    ctx.fillStyle = flowConfig.startBtnFillColor;
    drawRoundedRect(
      ctx,
      flowBuilderConstants.startBtnPosition.x,
      flowBuilderConstants.startBtnPosition.y,
      flowConfig.startBtnWidth,
      flowConfig.startBtnHeight,
      10
    );
    ctx.fill();
    // draw start icon
    ctx.fillStyle = flowConfig.startBtnTextColor;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    const iconSize = flowConfig.startBtnHeight - 28;
    drawRoundedTriangle(
      ctx,
      flowBuilderConstants.startBtnPosition.x + 20,
      flowBuilderConstants.startBtnPosition.y + 14,
      iconSize,
      iconSize,
      2
    );
    ctx.fill();
    // draw start text
    const startCenterY = flowBuilderConstants.startBtnPosition.y + flowConfig.startBtnHeight / 2;
    ctx.font = `400 16px '${flowConfig.fontFamily}'`;
    ctx.fillText(
      flowConfig.startBtnText,
      flowBuilderConstants.startBtnPosition.x + 40,
      startCenterY + 1
    );
    // draw start outgoing connector
    const startRight = flowBuilderConstants.startBtnPosition.x + flowConfig.startBtnWidth;
    ctx.strokeStyle = flowConfig.startBtnFillColor;
    ctx.lineWidth = flowConfig.itemStrokeWidth;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(startRight, startCenterY, flowConfig.itemConnectorOutRadius, 0, 2 * Math.PI,);
    ctx.fill();
  }
  if (initiateStepCardVisible) {
    ctx.beginPath();
    ctx.fillStyle = flowConfig.initiateStepCardFillColor;
    const height = flowConfig.stepCardHeight;
    const width = flowConfig.stepCardWidth;
    drawRoundedRect(
      ctx,
      flowBuilderConstants.initiateStepCardPosition.x,
      flowBuilderConstants.initiateStepCardPosition.y,
      width,
      height,
      20
    );

    ctx.fill();

    ctx.font = `400 22px '${flowConfig.fontFamily}'`;
    ctx.fillStyle = flowConfig.initiateStepCardTextColor;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillText(
      translate('job_scheduler_component.txt_initiate_journey'),
      flowBuilderConstants.initiateStepCardPosition.x + 90,
      flowBuilderConstants.initiateStepCardPosition.y + 114,
    );
    let iconX = flowBuilderConstants.initiateStepCardPosition.x + 56;
    let iconY = flowBuilderConstants.initiateStepCardPosition.y + 96;
    let iconSize = 24;
    drawCanvasImage(ctx, stepIconImage, iconX, iconY, iconSize, iconSize);

    iconSize = 45;
    iconX = flowBuilderConstants.initiateStepCardPosition.x + width / 2 - (iconSize / 2);
    iconY = flowBuilderConstants.initiateStepCardPosition.y + 32;
    drawCanvasImage(ctx, addIconImage, iconX, iconY, iconSize, iconSize);

    ctx.globalAlpha = 1;
    ctx.strokeStyle = flowConfig.initiateStepCardTextColor;
    ctx.setLineDash([4, 2]);
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // draw connectors after shapes so that they will be on top
  canvasElements.forEach((e) => e.drawConnectors(ctx));
};
